import React, { useEffect } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import Login from "./pages/Login";
// import Register from "./pages/Register";
import Settings from "./pages/Settings";
import LeadCalculator from "./pages/LeadCalculator";
import StartTracker from "./pages/StartTracker";
import TutionIncome from "./pages/TuitionIncome";
import DropOut from "./pages/DropOut";
import ClinicSales from "./pages/ClinicSales";
import ProfitLoss from "./pages/ProfitLoss";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminDashboard from "./pages/AdminDashboard";
import UserManagement from "./pages/UserManagement";
import AdminSettings from "./pages/AdminSettings";
import YearManagement from "./pages/YearManagement";
import LandingPage from "./pages/LandingPage";
import TrialForm from "./pages/TrialForm";
import TrialSuccess from "./pages/TrialSuccess";
import PricingManagement from "./pages/PricingManagement";
import TrialRequests from "./pages/TrialRequests";
import EmailCenter from "./pages/EmailCenter";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import ProgramDetails from "./pages/ProgramDetails";
import CheckoutForm from "./pages/CheckOutForm";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Success from "./components/payment-status/Success";
import Failed from "./components/payment-status/Failed";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { loadUserFromStorage, logout } from "./redux/reducers/authSlice";
import { getAuthToken } from "./utils/utils";
import Cancel from "./components/payment-status/Cancel";
import UnPaidCheckout from "./pages/UnPaidCheckout";
import { FiLogOut } from "react-icons/fi";
import { getUserById } from "./redux/reducers/userSlice";
import { syncUserStatus } from "./redux/reducers/syncSlice";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const CheckoutPage = () => (
  <Elements stripe={stripePromise}>
    <CheckoutForm />
  </Elements>
);

const App = () => {
  const dispatch = useDispatch();

  const { isAuthenticated, userData } = useSelector(
    (state) => state.auth ?? {}
  );
  const { role, status, paymentStatus, userId } = userData ?? {};

  useEffect(() => {
    dispatch(loadUserFromStorage());
  }, [dispatch]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) dispatch(logout());
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, [dispatch]);
  useEffect(() => {
    if (isAuthenticated) {
      // Initial sync
      dispatch(syncUserStatus());

      // Set up periodic sync every 5 minutes
      const syncInterval = setInterval(() => {
        dispatch(syncUserStatus());
        if (paymentStatus === "Unpaid") {
          window.location.reload();
        }
      }, 5 * 60 * 1000);

      return () => clearInterval(syncInterval);
    }
  }, [isAuthenticated, dispatch]);

  const isAuthorised = () => status === "Active";

  const isUserUnPaid = () => paymentStatus === "Unpaid";

  return (
    <Routes>
      {role === "Client" && (
        <>
          <Route
            path="/settings"
            element={
              isAuthenticated ? (
                <>
                  {isAuthorised() ? (
                    <Settings unPaid={isUserUnPaid()} />
                  ) : (
                    <BlockUser />
                  )}
                </>
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/lead-calculator"
            element={
              isAuthenticated ? (
                <>
                  {isAuthorised() ? (
                    <LeadCalculator unPaid={isUserUnPaid()} />
                  ) : (
                    <BlockUser />
                  )}
                </>
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <>
                  {isAuthorised() ? (
                    <LeadCalculator unPaid={isUserUnPaid()} />
                  ) : (
                    <BlockUser />
                  )}
                </>
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/start-tracker"
            element={
              isAuthenticated ? (
                <>
                  {isAuthorised() ? (
                    <StartTracker unPaid={isUserUnPaid()} />
                  ) : (
                    <BlockUser />
                  )}
                </>
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/tuition-income"
            element={
              isAuthenticated ? (
                <>
                  {isAuthorised() ? (
                    <TutionIncome unPaid={isUserUnPaid()} />
                  ) : (
                    <BlockUser />
                  )}
                </>
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/student-drop"
            element={
              isAuthenticated ? (
                <>
                  {isAuthorised() ? (
                    <DropOut unPaid={isUserUnPaid()} />
                  ) : (
                    <BlockUser />
                  )}
                </>
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/clinic-sales"
            element={
              isAuthenticated ? (
                <>
                  {isAuthorised() ? (
                    <ClinicSales unPaid={isUserUnPaid()} />
                  ) : (
                    <BlockUser />
                  )}
                </>
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/profit-loss"
            element={
              isAuthenticated ? (
                <>
                  {isAuthorised() ? (
                    <ProfitLoss unPaid={isUserUnPaid()} />
                  ) : (
                    <BlockUser />
                  )}
                </>
              ) : (
                <Login />
              )
            }
          />

          <Route
            path="/program-details"
            element={
              isAuthenticated ? (
                <>
                  {isAuthorised() ? (
                    <ProgramDetails unPaid={isUserUnPaid()} />
                  ) : (
                    <BlockUser />
                  )}
                </>
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/checkout"
            element={
              isAuthenticated ? (
                <> {isAuthorised() ? <CheckoutPage /> : <BlockUser />}</>
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/success"
            element={isAuthenticated ? <Success /> : <Login />}
          />
          <Route
            path="/failed"
            element={isAuthenticated ? <Failed /> : <Login />}
          />

          <Route
            path="/cancel"
            element={isAuthenticated ? <Cancel /> : <Login />}
          />
        </>
      )}
      {role === "Admin" && (
        <>
          <Route
            path="/"
            element={isAuthenticated ? <AdminDashboard /> : <Login />}
          />
          <Route
            path="/admin-dashboard"
            element={isAuthenticated ? <AdminDashboard /> : <Login />}
          />
          <Route
            path="/user-management"
            element={isAuthenticated ? <UserManagement /> : <Login />}
          />
          <Route
            path="/admin-settings"
            element={isAuthenticated ? <AdminSettings /> : <Login />}
          />
          <Route
            path="/year-management"
            element={isAuthenticated ? <YearManagement /> : <Login />}
          />
          <Route
            path="/pricing-management"
            element={isAuthenticated ? <PricingManagement /> : <Login />}
          />
          <Route
            path="/trial-requests"
            element={isAuthenticated ? <TrialRequests /> : <Login />}
          />
          <Route
            path="/email-center"
            element={isAuthenticated ? <EmailCenter /> : <Login />}
          />
          <Route
            path="/announcements"
            element={isAuthenticated ? <AnnouncementsPage /> : <Login />}
          />
        </>
      )}

      {/* Public routes — always accessible */}
      <Route path="/trial" element={isAuthenticated ? <Navigate to="/" /> : <TrialForm />} />
      <Route path="/trial/success" element={<TrialSuccess />} />
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <Login />} />
      <Route path="/" element={isAuthenticated ? <Navigate to="/" /> : <LandingPage />} />

      <Route
        path="/forgot-password"
        element={isAuthenticated ? <Navigate to="/" /> : <ForgotPassword />}
      />
      <Route
        path="/reset-password"
        element={isAuthenticated ? <Navigate to="/" /> : <ResetPassword />}
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default App;

const NotFound = () => {
  const navigate = useNavigate();
  return (
    <div className="flex h-screen items-center justify-center gap-2">
      <h2 className="text-3xl text-primary font-semibold ">
        404 No Page Found
      </h2>
      <p
        className=" text-textColor underline decoration-purple-500 flex items-center cursor-pointer select-none"
        onClick={() => {
          return navigate(-1);
        }}
      >
        Go Back
      </p>
    </div>
  );
};

const BlockUser = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };
  return (
    <div className="flex flex-col h-screen items-center justify-center gap-5">
      <h2 className="text-3xl text-primary font-semibold ">
        Your Account has been
        <span className="text-red-500 font-bold">De-Activated</span>.
      </h2>
      <p className=" text-blue-500  flex items-center cursor-pointer select-none">
        Please Contact Admin @
        <a
          href="mailto:Support@revenueproplanner.com"
          className="underline decoration-blue-500 font-bold"
        >
          Support@revenueproplanner.com
        </a>
      </p>
      <div>
        <button
          onClick={handleLogout}
          className={`flex items-center px-4 py-2 hover:bg-red-600 rounded-md text-white w-full bg-red-400 font-bold`}
        >
          <FiLogOut className="text-xl" />
          <span className="ml-3">Logout</span>
        </button>
      </div>
    </div>
  );
};
